import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { PowerBiApi } from '../models/power-bi-models';

@Injectable({ providedIn: 'root' })
export class PowerBIAPI {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = 'https://api.powerbi.com/v1.0/myorg';
  private readonly tenantId = '99c75c69-9ef2-4c4e-b5e3-54b6cf95c977';

  getWorkspaces(): Observable<PowerBiApi.GetWorkspacesResponse> {
    return this.http
      .get<PowerBiApi.GetWorkspacesResponse>(
        `${this.baseUrl}/groups`,
        { params: this.tenantParams() },
      )
      .pipe(
        map(response => ({
          ...response,
          value: response.value
            .map(workspace => ({
              ...workspace,
              displayName: this.getWorkspaceDisplayName(workspace.name),
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        })),
      );
  }

  getReports(workspaceId: string): Observable<PowerBiApi.GetReportsInWorkspaceResponse> {
    return this.http
      .get<PowerBiApi.GetReportsInWorkspaceResponse>(
        `${this.baseUrl}/groups/${workspaceId}/reports`,
        { params: this.tenantParams() },
      )
      .pipe(
        map(response => ({
          ...response,
          value: [...response.value].sort((a, b) => a.name.localeCompare(b.name)),
        })),
      );
  }

  getWorkspace(workspaceId: string): Observable<PowerBiApi.Workspace> {
    return this.http.get<PowerBiApi.Workspace>(
      `${this.baseUrl}/groups/${workspaceId}`,
      { params: this.tenantParams() },
    );
  }

  getReport(workspaceId: string, reportId: string): Observable<PowerBiApi.Report> {
    return this.http.get<PowerBiApi.Report>(
      `${this.baseUrl}/groups/${workspaceId}/reports/${reportId}`,
      { params: this.tenantParams() },
    );
  }

  getReportByName(workspaceId: string, reportName: string): Observable<PowerBiApi.Report | null> {
    const params = this.tenantParams().set(
      '$filter',
      `name eq '${reportName.replace(/'/g, "''")}'`,
    );

    return this.http
      .get<PowerBiApi.GetReportsInWorkspaceResponse>(
        `${this.baseUrl}/groups/${workspaceId}/reports`,
        { params },
      )
      .pipe(map(response => response.value[0] ?? null));
  }

  refreshUserPermissions(): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/refreshUserPermissions`,
      {},
      { params: this.tenantParams() },
    );
  }

  private tenantParams(): HttpParams {
    return new HttpParams().set('ctid', this.tenantId);
  }

  private getWorkspaceDisplayName(name: string): string {
    const displayName = name
      .replace(/^[0-9]+_/, '')
      .replace(/-[0-9]+$/, '');

    return displayName
      ? displayName.charAt(0).toUpperCase() + displayName.slice(1)
      : name;
  }
}